import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode
} from 'react';
import { CheckCircle2Icon, XIcon } from 'lucide-react';

interface ChallengeToastData {
  title: string;
  subtitle?: string;
  points: number;
}

interface ChallengeToastContextValue {
  showChallengeToast: (toast: ChallengeToastData) => void;
}

const ChallengeToastContext = createContext<ChallengeToastContextValue | null>(null);

/**
 * Access the challenge toast. Returns a no-op when no provider is mounted,
 * so non-hunt pages and tests are unaffected.
 */
export function useChallengeToast(): ChallengeToastContextValue {
  const ctx = useContext(ChallengeToastContext);
  if (!ctx) {
    return { showChallengeToast: () => {} };
  }
  return ctx;
}

/**
 * Fixed-position toast provider that shows "challenge complete +N pts"
 * notifications. Mounted once at the app root so any page can trigger it.
 */
export function ChallengeToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ChallengeToastData | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const dismiss = useCallback(() => {
    setToast(null);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const showChallengeToast = useCallback(
    (data: ChallengeToastData) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      setToast(data);
      timerRef.current = setTimeout(() => setToast(null), 4000);
    },
    []
  );

  return (
    <ChallengeToastContext.Provider value={{ showChallengeToast }}>
      {children}
      {toast && (
        <div
          className="fixed bottom-[calc(1rem+env(safe-area-inset-bottom))] left-1/2 z-[100] -translate-x-1/2 animate-in fade-in slide-in-from-bottom-4 duration-300"
          role="status"
          aria-live="polite"
        >
          <div className="flex items-center gap-3 rounded-xl border bg-card px-4 py-3 shadow-lg">
            <CheckCircle2Icon className="h-5 w-5 shrink-0 text-green-500" />
            <div className="min-w-0">
              <p className="text-sm font-semibold">{toast.title}</p>
              {toast.subtitle && (
                <p className="truncate text-xs text-muted-foreground">{toast.subtitle}</p>
              )}
            </div>
            <Badge>+{toast.points} pts</Badge>
            <button
              onClick={dismiss}
              className="ml-1 rounded-full p-1 text-muted-foreground hover:bg-muted"
              aria-label="Dismiss"
            >
              <XIcon className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}
    </ChallengeToastContext.Provider>
  );
}

function Badge({ children }: { children: ReactNode }) {
  return (
    <span className="rounded-full bg-amber-500/15 px-2.5 py-0.5 text-sm font-bold text-amber-600 dark:text-amber-400">
      {children}
    </span>
  );
}
